import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import { storeName as layoutModelStore } from './layout.model';
import { storeName as homeProfileStore } from './homeProfile.model';
import { RootModel } from '../models';
import { storeName as projectEntityStore } from '../entities/project.entity';
import { IframeContentDataTypes, StoreImageTypes } from '../../types';
import {
  buildHashUrl,
  changeLanguageUrl,
  getHashParams,
  getLanguagePathByContent,
  getRelativePathUrl,
} from '../../utils';
import {
  CONTENT_KEY,
  MAGICAL_KEY,
  MODULE_PAGE_LINK,
  ABOUT_LANGUAGE_PATH,
  SITEPLAN_LANGUAGE_PATH,
  AMENITIES_LANGUAGE_PATH,
  LOCATION_LANGUAGE_PATH,
  LanguageTypes,
} from '../../commons/constants';
import { TemplateType } from '../../types/pageLayout.type';
import { getWidgetTemplate } from '../../services/project.service';
import { getShortenLink } from '../../services/shortenLink.service';

export const storeName = 'projectModel';

const VIRTUAL_TOUR_HOST = 'virtualtour.clik.vn';

type ProjectModelTypes = {
  projectId: number;
  projectCode: string;
  projectKey: string;
  // embedded iframe data
  iframeContentData: IframeContentDataTypes;
  // active key tab
  contentActiveKey: string;
  isProjectPage: boolean;
  // fullscreen gallery item
  fullScreenMedia: StoreImageTypes[];
  fillScreenActiveIdx: number;
  locationGroupsIds: number[];
  newsIds: number[];
  isShowMenu: boolean;
  isShowUserContact: boolean;
  isShare: boolean;
};

const initialState: ProjectModelTypes = {
  projectId: null,
  projectCode: null,
  projectKey: null,
  // embedded iframe data
  iframeContentData: { visible: false, url: null },
  // active key tab
  contentActiveKey: null,
  isProjectPage: false,
  // fullscreen gallery item
  fullScreenMedia: null,
  fillScreenActiveIdx: null,
  // location groups
  locationGroupsIds: null,
  newsIds: null,
  isShowMenu: false,
  isShowUserContact: false,
  isShare: false,
};

export const projectModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setProject: produce((state, payload: { id: number; code: string; key: string }) => {
      if (payload.id) state.projectId = payload.id;
      if (payload.code) state.projectCode = payload.code;
      if (payload.key) state.projectKey = payload.key;
    }),
    setIframeContentData: produce((state, payload: IframeContentDataTypes) => {
      if (!payload) {
        state.iframeContentData = initialState.iframeContentData;
        return;
      }
      state.iframeContentData = payload;
    }),
    setContentActiveKey: produce((state, payload: { key: string; isProjectPage?: boolean }) => {
      const { key, isProjectPage = false } = payload;
      state.contentActiveKey = key;
      state.isProjectPage = isProjectPage;
    }),
    setNewsIds: produce((state, payload: number[]) => {
      state.newsIds = payload;
    }),
    setFullscreenGallery: produce((state, payload: { media: StoreImageTypes[]; idx: number }) => {
      if (!payload) {
        state.fullScreenMedia = initialState.fullScreenMedia;
        state.fillScreenActiveIdx = initialState.fillScreenActiveIdx;
        return;
      }

      state.fullScreenMedia = payload.media;
      state.fillScreenActiveIdx = payload.idx;
    }),
    setLocationGroupIds: produce((state, payload: number[]) => {
      state.locationGroupsIds = payload;
    }),
    setShowMenu: produce((state, payload: boolean) => {
      state.isShowMenu = payload;
    }),
    setShowUserContact: produce((state, payload: boolean) => {
      state.isShowUserContact = payload;
    }),
    setShareLink: produce((state, payload: boolean) => {
      state.isShare = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async checkProjectPublic(payload, state) {
      const { projectKey, token } = payload;
      let result: any;
      if (token === MAGICAL_KEY) {
        // request API for get project by [Project Key]
        result = await dispatch[projectEntityStore].loadByKey(projectKey);
      }

      // request API for check [Project Key] and [Token]
      result = await dispatch[projectEntityStore].checkAuthorizeProject({
        id: state[storeName].projectId,
        key: projectKey,
        token,
      });

      if (result.notFound) return result;

      await this.setProject({ id: result.id, code: result.code, key: result.alias || result.code });

      return result;
    },
    set360TrackingParams(url: string, state) {
      const { contentActiveKey } = state[storeName];

      if (!url) return url;

      const temp = new URL(url);
      if (!temp.hostname.includes(VIRTUAL_TOUR_HOST)) return url;

      const hashParams = getHashParams();

      if (hashParams && !Object.keys(hashParams).includes('vt')) {
        buildHashUrl({
          ...hashParams,
          vt: url,
        });
      }

      // Ignore logic add tracking vt 360 if current screen as HOME PROFILE
      if (contentActiveKey === CONTENT_KEY.HOME_PROFILE.key) return url;
      const { projectCode, projectId } = state[storeName];
      if (!projectCode || !projectId) {
        return url;
      }
      const { name: projectName } = state[projectEntityStore].ids[projectId];

      if (projectCode) temp.searchParams.set('project', projectCode);
      if (projectName) temp.searchParams.set('project_name', projectName);

      temp.hash = temp.hash.startsWith('#')
        ? `&url=${window.location.href}`
        : `#url=${window.location.href}`;
      return temp.href;
    },
    setIframeContent(dataIframe: IframeContentDataTypes, state) {
      if (!dataIframe) {
        const hashParams = getHashParams();
        if (Object.keys(hashParams).includes('vt')) {
          delete hashParams['vt'];
          buildHashUrl(hashParams);
        }
        dispatch[storeName].setIframeContentData(initialState.iframeContentData);
        dispatch[layoutModelStore].setChatVisible(true);
        return;
      }
      dataIframe.url = this.set360TrackingParams(dataIframe.url);

      const currentLocale = state[layoutModelStore].locale;
      dataIframe.url = changeLanguageUrl(dataIframe.url, currentLocale.locale);
      dispatch[storeName].setIframeContentData(dataIframe);

      dispatch[layoutModelStore].setChatVisible(false);
    },
    setFullscreenGalleryData(payload: { media: StoreImageTypes[]; selectedData: StoreImageTypes }) {
      if (!payload) {
        dispatch[storeName].setFullscreenGallery();
        return;
      }

      const selectedIdx = payload.media.findIndex(
        (i) => payload.selectedData && payload.selectedData.originalName === i.originalName
      );

      this.setFullscreenGallery({
        media: payload.media,
        idx: selectedIdx !== -1 ? selectedIdx : null,
      });
    },
    redirectPageContent(payload, state) {
      const { contentParam, isProjectPage = false, history } = payload;
      if (!contentParam) return;

      const link = this.getHomePageLink({ link: MODULE_PAGE_LINK, isProjectPage })
        .replace(':activeKey', contentParam)
        .replace(':projectKey', state[storeName].projectKey);
      if (history && !!link) history.push(link);
    },
    getHomePageLink(payload, state): string {
      const { link, isProjectPage = false } = payload;
      if (!link) return '';

      if (state[storeName].isProjectPage || isProjectPage) return getRelativePathUrl(link);
      return link;
    },
    setActiveContentKey(payload, state) {
      if (!payload || !payload.key) return;

      const oldKey = state[storeName].contentActiveKey;
      // purge gallery when changing tab
      if (oldKey !== payload.key) this.setFullscreenGallery();
      // when view visualization change tab reset data iframeContentData
      this.setIframeContentData();
      this.setContentActiveKey(payload);
    },

    redirectAdditionalInformation({ key, history }, state) {
      const isSelfHomeProfile = state[homeProfileStore].isSelf;
      const language: LanguageTypes = state[layoutModelStore].locale;

      if (!key || !CONTENT_KEY[key]) return;

      if (state[storeName].isProjectPage) {
        const languagePath = getLanguagePathByContent(CONTENT_KEY[key]);
        this.redirectPageContent({
          contentParam: languagePath[language.locale.toUpperCase()],
          history,
        });
      } else {
        let link: string;

        switch (key) {
          case CONTENT_KEY.ABOUT.key:
            link = MODULE_PAGE_LINK.replace(
              ':activeKey',
              ABOUT_LANGUAGE_PATH[language.locale.toUpperCase()]
            );
            break;
          case CONTENT_KEY.SITEPLAN.key:
            link = MODULE_PAGE_LINK.replace(
              ':activeKey',
              SITEPLAN_LANGUAGE_PATH[language.locale.toUpperCase()]
            );
            break;
          case CONTENT_KEY.AMENITIES.key:
            link = MODULE_PAGE_LINK.replace(
              ':activeKey',
              AMENITIES_LANGUAGE_PATH[language.locale.toUpperCase()]
            );
            break;
          case CONTENT_KEY.LOCATION.key:
            link = MODULE_PAGE_LINK.replace(
              ':activeKey',
              LOCATION_LANGUAGE_PATH[language.locale.toUpperCase()]
            );
            break;

          default:
            break;
        }

        const newLink =
          link &&
          this.getHomePageLink({ link }).replace(':projectKey', state[storeName].projectKey);
        if (!newLink) {
          return;
        }
        /**
         * When user access homeprofile by route /can-ho (isSelf === true)
         * Show content of additional information on iframe
         * Open new-tab when isSelf === false
         */
        if (isSelfHomeProfile) {
          this.setIframeContent({
            visible: true,
            url: `${process.env.REACT_APP_DOMAIN}${newLink}`,
          });
          return;
        }
        window.open(newLink, '_blank');
      }
    },
    async getTemplate(token: string, state) {
      let template: TemplateType = {
        errMsg: '',
        content: '',
      };
      try {
        const { locale } = state[layoutModelStore];
        template = await getWidgetTemplate(token, locale.apiLanguage);
        return template;
      } catch (err) {
        return template;
      }
    },

    async getShortenLink(payload: string) {
      const resShortenLink = await getShortenLink(payload);
      return resShortenLink;
    },
  }),
});

export default projectModel;
